import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Building2,
  CalendarCheck2,
  Gauge,
  GraduationCap,
  UploadCloud,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import { apiFetch, queryString } from "../lib/api";
import type { AlertRecord, DashboardMetrics } from "../shared/schemas";
import { EmptyState, ErrorState, LoadingState, PageHeader, PriorityBadge } from "../components/ui";

interface RoomSummary {
  room: string;
  campus: string;
  averageUtilization: number;
  bookingCount: number;
}

export function DashboardPage() {
  const { snapshotTermId, departmentId, terms, user } = useAppContext();
  const canImport = user.role === "administrator" || user.role === "scheduler";
  const importAction = canImport ? (
    <Link className="button-primary" to="/imports">
      <UploadCloud className="h-4 w-4" />
      Import schedule
    </Link>
  ) : undefined;
  const metricsQuery = useQuery({
    queryKey: ["dashboard", snapshotTermId, departmentId],
    queryFn: () =>
      apiFetch<DashboardMetrics>(
        `/dashboard${queryString({ snapshotTermId, department: departmentId })}`,
      ),
    enabled: Boolean(snapshotTermId),
  });
  const alertsQuery = useQuery({
    queryKey: ["alerts", snapshotTermId, departmentId, "open"],
    queryFn: () =>
      apiFetch<AlertRecord[]>(
        `/alerts${queryString({
          snapshotTermId,
          department: departmentId,
          status: "open",
        })}`,
      ),
    enabled: Boolean(snapshotTermId),
  });
  const roomsQuery = useQuery({
    queryKey: ["rooms", snapshotTermId, departmentId],
    queryFn: () =>
      apiFetch<RoomSummary[]>(
        `/rooms${queryString({ snapshotTermId, department: departmentId })}`,
      ),
    enabled: Boolean(snapshotTermId),
  });

  if (!snapshotTermId || terms.length === 0) {
    return (
      <>
        <PageHeader
          title="Executive Dashboard"
          description="Institutional scheduling health and room utilization."
          actions={importAction}
        />
        <EmptyState title="No scheduling snapshot" description="Import a CSV or Excel scheduling report to activate the dashboard." />
      </>
    );
  }
  if (metricsQuery.isLoading) return <LoadingState />;
  if (metricsQuery.error || !metricsQuery.data) return <ErrorState error={metricsQuery.error} />;

  const metrics = metricsQuery.data;
  const fillRate = metrics.totalCapacity > 0 ? metrics.totalEnrollment / metrics.totalCapacity : 0;
  const cards = [
    { label: "Active Sections", value: metrics.totalSections.toLocaleString(), detail: `Term ${metrics.termCode}`, icon: CalendarCheck2, color: "text-brand-600 bg-brand-50", to: "/sections" },
    { label: "Total Enrollment", value: metrics.totalEnrollment.toLocaleString(), detail: `${(fillRate * 100).toFixed(1)}% of scheduled capacity`, icon: Users, color: "text-sky-600 bg-sky-50", to: "/sections" },
    { label: "Active Rooms", value: metrics.activeRooms.toLocaleString(), detail: "Distinct physical spaces", icon: Building2, color: "text-emerald-600 bg-emerald-50", to: "/rooms" },
    { label: "Average Utilization", value: `${(metrics.averageRoomUtilization * 100).toFixed(1)}%`, detail: "Booking-level maximum / room cap", icon: Gauge, color: "text-violet-600 bg-violet-50", to: "/rooms" },
    { label: "Room Conflicts", value: metrics.conflictCount.toLocaleString(), detail: "Ungrouped overlaps", icon: AlertTriangle, color: "text-rose-600 bg-rose-50", to: "/timetable" },
    { label: "Waitlist Bottlenecks", value: metrics.bottleneckCount.toLocaleString(), detail: "No seats and WPI >= 80%", icon: GraduationCap, color: "text-amber-600 bg-amber-50", to: "/bottlenecks" },
  ];

  return (
    <>
      <PageHeader
        title="Executive Dashboard"
        description="A current operational view of enrollment, physical room demand, conflicts, and bottleneck pressure."
        actions={importAction}
      />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => (
          <Link
            key={card.label}
            to={card.to}
            className="panel flex items-center justify-between p-5 transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{card.label}</p>
              <p className="mt-2 text-3xl font-black tracking-tight text-slate-950">{card.value}</p>
              <p className="mt-1 text-xs text-slate-500">{card.detail}</p>
            </div>
            <div className={`rounded-2xl p-3 ${card.color}`}><card.icon className="h-6 w-6" /></div>
          </Link>
        ))}
      </div>
      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <section className="panel p-5">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="font-bold text-slate-900">Room Allocation Health</h2>
              <p className="text-xs text-slate-500">Highest average utilization among active rooms.</p>
            </div>
            <span className="badge bg-slate-100 text-slate-700">
              {metrics.underutilizedCount} underutilized
            </span>
          </div>
          <div className="space-y-1">
            {(roomsQuery.data ?? []).slice(0, 8).map((room) => (
              <Link
                key={`${room.campus}:${room.room}`}
                className="block rounded-lg p-2 transition hover:bg-slate-50"
                to={`/timetable?room=${encodeURIComponent(room.room)}`}
              >
                <div className="mb-1.5 flex justify-between text-xs">
                  <span className="font-semibold text-slate-700">{room.campus} / {room.room}</span>
                  <span className="text-slate-500">{(room.averageUtilization * 100).toFixed(0)}% - {room.bookingCount} bookings</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${room.averageUtilization > 1 ? "bg-rose-500" : room.averageUtilization >= 0.7 ? "bg-emerald-500" : "bg-amber-500"}`}
                    style={{ width: `${Math.min(room.averageUtilization * 100, 100)}%` }}
                  />
                </div>
              </Link>
            ))}
            {roomsQuery.data?.length === 0 && <p className="py-10 text-center text-sm text-slate-400">No physical rooms in this snapshot.</p>}
          </div>
        </section>
        <section className="panel p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-bold text-slate-900">Priority Alerts</h2>
              <p className="text-xs text-slate-500">Open items requiring scheduling attention.</p>
            </div>
            <span className="badge bg-rose-100 text-rose-800">{alertsQuery.data?.length ?? 0} open</span>
          </div>
          <div className="divide-y divide-slate-100">
            {(alertsQuery.data ?? []).slice(0, 7).map((alert) => (
              <Link
                key={alert.id}
                className="block rounded-lg py-3 transition hover:bg-slate-50 hover:px-2"
                to={
                  alert.conflictId
                    ? `/timetable?conflict=${encodeURIComponent(alert.conflictId)}`
                    : alert.sectionId
                      ? `/timetable?section=${encodeURIComponent(alert.sectionId)}`
                      : "/alerts"
                }
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-slate-800">{alert.title}</p>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">{alert.message}</p>
                  </div>
                  <PriorityBadge priority={alert.priority} />
                </div>
              </Link>
            ))}
            {alertsQuery.data?.length === 0 && <p className="py-10 text-center text-sm text-slate-400">No open alerts.</p>}
          </div>
        </section>
      </div>
    </>
  );
}
